import React from "react";
import { useAuth } from "@clerk/nextjs";
import { ButtonVariant, CoButton } from "components/CoButton";
import { usePremiumFeatureModal } from "features/Subscription/components";
import Tooltip from "components/Tooltip";
import path from "constants/path";
import useSubscription from "features/User/hooks/useSubscription";
import {
  useSubscriptionUpgradeTarget,
  SubscriptionPlanIcon,
} from "features/Pricing";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import { useSidebar } from "hooks/useSidebar";
import { useAppDispatch, useAppSelector } from "hooks/useStore";
import { PanelLeft } from "lucide-react";
import Link from "next/link";
import { setIsSearching } from "store/slices/search";
import { CreditedFeature } from "store/slices/subscription";
import { SidebarContentAccountMenu } from "./SidebarContentAccountMenu";
import SignedInContent from "./SignedInContent";
import SignedOutContent from "./SignedOutContent";
import { LogoMark } from "design_system/logo";

function SidebarContent() {
  const dispatch = useAppDispatch();
  const { toggleSidebar } = useSidebar();
  const [sidebarLabels] = useLabels("screens.sidebar");
  const isMobile = useAppSelector((state) => state.setting.isMobile);

  const { isSignedIn, isLoaded } = useAuth();

  const {
    isSubscriptionPremium,
    isSubscriptionPaused,
    isSubscriptionPausedAndNotPremium,
  } = useSubscription();

  const { target: upgradeTarget } = useSubscriptionUpgradeTarget();

  const closeSidebar = () => {
    toggleSidebar();
  };

  const handleClickHome = () => {
    if (isMobile) {
      closeSidebar();
    }
    dispatch(setIsSearching(false));
  };

  const { openPremiumFeatureModal } = usePremiumFeatureModal();

  const onClickUpgradeToPremium = () => {
    openPremiumFeatureModal({
      feature: CreditedFeature.PRO_SEARCH,
      actionSource: MixpanelActionSource.Sidebar,
    });
  };

  if (!isLoaded) return null;

  return (
    <div className="flex overflow-x-hidden flex-col h-full min-w-(--sidebar-width)">
      <div className="flex justify-between items-center pr-2 pl-4 min-h-header">
        <Link href={path.HOME} passHref legacyBehavior>
          <a onClick={handleClickHome} className="flex items-center h-full">
            <LogoMark height={24} />
          </a>
        </Link>
        <Tooltip content={sidebarLabels["close-sidebar"]} placement="bottom">
          <CoButton
            aria-label={sidebarLabels["close-sidebar"]}
            variant={ButtonVariant.Flat}
            size="sm"
            icon={<PanelLeft className="text-fg-base" size={20} />}
            onClick={closeSidebar}
            className="cursor-w-resize"
          />
        </Tooltip>
      </div>
      <>{isSignedIn ? <SignedInContent /> : <SignedOutContent />}</>
      <div className="flex relative flex-col whitespace-nowrap bg-bg-base">
        {isSignedIn && <SidebarContentAccountMenu />}
        {isSubscriptionPausedAndNotPremium && (
          <CoButton
            variant={ButtonVariant.Primary}
            label={sidebarLabels["resume-membership"]}
            onClick={onClickUpgradeToPremium}
            size="sm"
            className="mx-4"
          />
        )}
        {!isSubscriptionPremium && !isSubscriptionPaused && isSignedIn && (
          <CoButton
            icon={<SubscriptionPlanIcon plan={upgradeTarget} />}
            variant={ButtonVariant.Tertiary}
            label={sidebarLabels["upgrade-to-premium"]}
            onClick={onClickUpgradeToPremium}
            size="sm"
            className="mx-4 mb-3"
          />
        )}
      </div>
    </div>
  );
}

export default SidebarContent;
